import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { API } from "../config/api";

const TRACKED = new Set(["QQQ", "SPY", "DIA", "IWM"]);

/**
 * Intraday benchmark-gap card for QQQ/SPY/DIA/IWM.
 *
 * Renders nothing for any other ticker so it's safe to drop unconditionally on
 * the Stock Analysis page. Polls every 30s during market hours so the gap
 * stays fresh as the day progresses.
 */
export default function BenchmarkGapCard({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker || !TRACKED.has(ticker.toUpperCase())) {
      setData(null);
      setLoading(false);
      return;
    }
    let canc = false;
    let timer = null;

    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/etf-benchmark-gap/${ticker}`, { timeout: 20000 });
        if (!canc) setData(r.data);
      } catch {
        if (!canc) setData(null);
      } finally {
        if (!canc) setLoading(false);
      }
    };

    fetchOnce();
    // Refresh every 30s — same as the backend cache window
    timer = setInterval(fetchOnce, 30000);
    return () => {
      canc = true;
      if (timer) clearInterval(timer);
    };
  }, [ticker]);

  if (!ticker || !TRACKED.has(ticker.toUpperCase())) return null;
  if (loading) {
    return (
      <Card className="bg-slate-900/60 border-slate-800" data-testid="benchmark-gap-card-loading">
        <CardContent className="py-3 text-xs text-slate-500 flex items-center gap-2">
          <RefreshCw className="w-3 h-3 animate-spin" /> Loading benchmark gap…
        </CardContent>
      </Card>
    );
  }
  if (!data?.ok) {
    return (
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="py-3 text-xs text-slate-500">
          Benchmark gap unavailable: {data?.reason || "unknown"}.
        </CardContent>
      </Card>
    );
  }

  const gap = data.gap_pct;
  const isAligned = data.bias === "ALIGNED";
  const isExpectDown = data.bias === "EXPECT DOWN";
  const isExpectUp = data.bias === "EXPECT UP";

  // Strong bias -> brighter color; aligned -> grey
  const tone = isAligned
    ? "from-slate-800 to-slate-900 border-slate-700"
    : isExpectDown
    ? "from-red-950/70 to-orange-950/40 border-red-700/60"
    : "from-emerald-950/70 to-green-950/40 border-emerald-700/60";

  const Icon = isAligned ? Minus : isExpectDown ? TrendingDown : TrendingUp;
  const iconColor = isAligned ? "text-slate-400" : isExpectDown ? "text-red-300" : "text-emerald-300";
  const biasColor = isAligned ? "text-slate-400" : isExpectDown ? "text-red-300" : "text-emerald-300";

  const strengthBadge = {
    LARGE: "bg-amber-700/60 text-amber-100",
    NOTABLE: "bg-amber-700/40 text-amber-200",
    MILD: "bg-slate-700/60 text-slate-200",
    FLAT: "bg-slate-800 text-slate-500",
  }[data.strength] || "bg-slate-800 text-slate-500";

  return (
    <Card
      className={`bg-gradient-to-br ${tone} border-2`}
      data-testid="benchmark-gap-card"
    >
      <CardContent className="py-3 space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${iconColor}`} />
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">
                ETF Benchmark Gap · {data.ticker} vs {data.benchmark_label}
              </div>
              <div className={`font-bold text-lg ${biasColor}`} data-testid="benchmark-gap-bias">
                {data.bias === "ALIGNED" ? "Aligned" : data.bias.replace("EXPECT", "Expect")}
              </div>
            </div>
          </div>
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${strengthBadge}`}>
            {data.strength}
          </span>
        </div>

        {/* Big gap number */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Gap (ETF − Index)</div>
            <div
              className={`text-3xl font-bold tabular-nums ${
                isAligned ? "text-slate-300" : isExpectDown ? "text-red-300" : "text-emerald-300"
              }`}
              data-testid="benchmark-gap-value"
            >
              {gap >= 0 ? "+" : ""}
              {gap.toFixed(3)}%
            </div>
            <div className="text-[10px] text-slate-500">≈ {data.gap_bps} bps</div>
          </div>

          <div className="border-l border-slate-700 pl-3">
            <div className="text-[10px] uppercase text-slate-500">{data.ticker} day</div>
            <div
              className={`text-lg font-semibold tabular-nums ${
                data.etf_pct_change >= 0 ? "text-emerald-300" : "text-red-300"
              }`}
            >
              {data.etf_pct_change >= 0 ? "+" : ""}
              {data.etf_pct_change.toFixed(3)}%
            </div>
            <div className="text-[10px] text-slate-500">${data.etf_price.toFixed(2)}</div>
          </div>

          <div>
            <div className="text-[10px] uppercase text-slate-500">{data.benchmark_label} day</div>
            <div
              className={`text-lg font-semibold tabular-nums ${
                data.benchmark_pct_change >= 0 ? "text-emerald-300" : "text-red-300"
              }`}
            >
              {data.benchmark_pct_change >= 0 ? "+" : ""}
              {data.benchmark_pct_change.toFixed(3)}%
            </div>
            <div className="text-[10px] text-slate-500">
              {data.benchmark_price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Rationale */}
        <div className="text-xs text-slate-300/90 bg-slate-950/50 p-2 rounded border border-slate-800">
          {data.rationale}
        </div>
      </CardContent>
    </Card>
  );
}
